// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import moment from 'moment';

import DatePicker from 'wdna-date-selector';

import { GridApi, RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';

import { weather } from '../../../../sensors/requests';

interface Props {
  id: number | undefined;
  show: boolean;
  onClose: () => void;
}

interface State {
  flatpickrMode: string;
  groupDateTime: string;

  date: Date[];
  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  data: any[];
}

export default class Graph extends React.Component<Props, State> {
  private pid: string;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      data: []
    };
  }

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public submit = (): void => {
    const { dateIni, timeIni, dateEnd, timeEnd } = this.state;
    const idSensor: number | undefined = this.props.id;
    const req1: Record<string, any> = {};

    if (dateIni) req1.from = `${dateIni}${(timeIni) ? `T${timeIni}:00.000Z` : ''}`;
    if (dateEnd) req1.to = `${dateEnd}${(timeEnd) ? `T${timeEnd}:00.000Z` : ''}`;

    req1.values = [0, 1, 2, 2, 2, 4, 3, 6, 5, 8];
    req1.operators = ['amean', 'mean', 'mean', 'max', 'min', 'sum', 'mean', 'mean', 'mean', 'max'];
    req1.group = 1440;
    req1.round = false;

    this.loadingStart();
    weather('statistics', String(idSensor), req1)
      ?.then((resp: any[]) => {
        resp.splice(0, 1);
        this.setState({
          data: resp,
        });
      })
      .catch((error: any) => {
        messageError('Error', error.message);
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleDatePickerChange = (config: any, dateKey: string = 'dateExport'): void => {
    const date: Date[] = config.getDates();
    const preDataIni: Date = date[0];
    const newDataIni: string = moment(preDataIni).format('YYYY-MM-DD');
    const preDataEnd: Date = date[1];
    let newDataEnd: string = '';
    if (config.getMode() === 'range') {
      newDataEnd = moment(preDataEnd).format('YYYY-MM-DD');
    } else {
      newDataEnd = moment(newDataIni).add(1, 'days').format('YYYY-MM-DD');
    }

    this.setState((prevState: State) => ({
      ...prevState,
      [dateKey]: date,
      flatpickrMode: config.getMode(),
      dateIni: newDataIni,
      dateEnd: newDataEnd
    }));
  };

  public dateCellRenderer = (params: any): string => {
    let { value } = params;
    [value] = value.split(' ');
    return value;
  };

  public numberCellRenderer = (params: any): string => {
    let { value } = params;
    value = !isNaN(value) ? value && value.toFixed(2) : '';
    return value;
  };

  public numberCellRenderer2 = (params: any): string => {
    let { value } = params;
    value = !isNaN(value) ? value && (value * 1000).toFixed(2) : '';
    return value;
  };

  public rowDataChanged = (event: RowDataChangedEvent): void => {
    const gridApi: GridApi = event.api;
    setTimeout((): void => {
      gridApi.sizeColumnsToFit();
    }, 100);
  };

  public render(): JSX.Element {
    const { show, onClose } = this.props;
    const { date, data, flatpickrMode, groupDateTime } = this.state;

    const columnAlias2: Record<number, string> = {
      0: 'Date',
      1: 'Count',
      2: 'W-Speed Mean (km/h)',
      3: 'T (ºC)',
      4: 'T Min (ºC)',
      5: 'T Max (ºC)',
      6: 'HR (%)',
      7: 'P (hPa)',
      8: 'R_s (W/m2)',
      9: 'W-Gust (km/h)',
      10: 'Pr Sum (l/m2)',
      11: 'W-Dir Mean (º)',
      12: 'ET (mm)',
    };
    const columnsToHide2: string[] = ['1'];
    const columnStyles2: Record<number, any> = {
      0: { textAlign: 'center' },
    };
    const customColumnsDefs2: Record<number, any> = {
      0: { cellRendererFramework: this.dateCellRenderer },
      2: { cellRendererFramework: this.numberCellRenderer },
      3: { cellRendererFramework: this.numberCellRenderer },
      4: { cellRendererFramework: this.numberCellRenderer },
      5: { cellRendererFramework: this.numberCellRenderer },
      6: { cellRendererFramework: this.numberCellRenderer },
      7: { cellRendererFramework: this.numberCellRenderer2 },
      8: { cellRendererFramework: this.numberCellRenderer },
      9: { cellRendererFramework: this.numberCellRenderer },
      10: { cellRendererFramework: this.numberCellRenderer },
      11: { cellRendererFramework: this.numberCellRenderer },
      12: { cellRendererFramework: this.numberCellRenderer },
    };

    return (
      <ContainerModal
        fullScreenButton={false}
        show={show}
        title="History Report"
        modalClass="selector-modal"
        onClose={onClose}
        footerButtons={[
          {
            text: 'OK',
            className: 'btn btn-primary',
            onClick: onClose,
          },
        ]}
      >
        <div className="row">
          <div className="col-md-6">
            <DatePicker
              icon
              hours={[]}
              dates={date}
              showChangeModes
              mode={flatpickrMode}
              granularity={groupDateTime}
              iconClass="wdna-ico-icon-calendar"
              handleChanges={this.handleDatePickerChange}
            />
          </div>
          <div className="col-md-6">
            <button type="button" className="filter-btn btn btn-default" onClick={this.submit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </div>
        <br />
        <header>
          <span className="widget-icon" style={{ padding: '8px' }}>
            <i className="fa fa-edit" />
          </span>
          Daily Summary Data
        </header>
        <Table
          data={data || []}
          alias={columnAlias2}
          customCellStyles={columnStyles2}
          hiddenCols={columnsToHide2}
          rowDataChanged={this.rowDataChanged}
          customColumnsDefs={customColumnsDefs2}
          height="calc(50vh - 7px)"
          pagination
        />
      </ContainerModal>
    );
  }
}
